/**
 * Block Dimensions Configuration
 *
 * Landscape 4:3 aspect ratio for strategy blocks (wider than tall).
 * Provides more vertical space than 2:1 ratio, better for text-heavy descriptions.
 *
 * Different from SCREEN_DIMENSIONS which are portrait (taller than wide, ~9:19 ratio).
 *
 * @module blockDimensionConfig
 */

/**
 * Block dimensions for ReactFlow canvas nodes
 *
 * Aspect ratio: 4:3 (landscape)
 * - WIDTH: 422px (wide, same as screen height)
 * - HEIGHT: 316px (4:3 ratio: 422 × 3/4)
 * - ASPECT_RATIO: 1.336 (landscape)
 */
export const BLOCK_DIMENSIONS = {
  WIDTH: 422,   // Landscape width
  HEIGHT: 316,  // 4:3 ratio (422 × 3/4 = 316.5 ≈ 316)
  get ASPECT_RATIO() {
    return this.WIDTH / this.HEIGHT;  // 1.336 (4:3)
  }
} as const;

/**
 * Block layout configuration
 *
 * Defines internal spacing and structural dimensions for block rendering
 */
export const BLOCK_LAYOUT = {
  /** Height of header area containing block name and type badge */
  HEADER_HEIGHT: 40,

  /** Padding around description text content (in pixels) */
  CONTENT_PADDING: 12,

  /** Border radius for rounded corners */
  BORDER_RADIUS: 8,

  /** Border width around block */
  BORDER_WIDTH: 2,

  /** Height of colored accent bar at bottom */
  FOOTER_HEIGHT: 3,
} as const;

/**
 * Preview scale factors for blocks in different contexts
 *
 * Used to scale block previews appropriately depending on where they're displayed
 */
export const BLOCK_PREVIEW_SCALE = {
  /** Small preview in template panel (left sidebar) */
  TEMPLATE_PANEL: 0.3,

  /** Full size on ReactFlow canvas */
  CANVAS_NODE: 1.0,

  /** Full size in detail/edit view */
  DETAIL_VIEW: 1.0,
} as const;

/**
 * Block handle positioning regions
 *
 * Defines positioning constraints for connection handles on blocks.
 * Calculated proportionally based on BLOCK_DIMENSIONS.HEIGHT (316px).
 *
 * Different from HANDLE_REGIONS (screens) because blocks are landscape (4:3)
 * while screens are portrait (~9:19).
 *
 * Usage in BlockConverterService:
 * - DEFAULT_CENTER: Single handle position (centered vertically)
 * - MIDDLE_START/END: Multiple handles (vertically distributed)
 * - TOP_START: Top region for special handles
 * - BOTTOM_OFFSET: Bottom offset for footer-like handles
 */
export const BLOCK_HANDLE_REGIONS = {
  /** Top region start (9.5% of height = 30px / 316px) */
  TOP_START: 30,

  /** Middle region start (23.7% of height = 75px / 316px) */
  MIDDLE_START: 75,

  /** Middle region end (63.3% of height = 200px / 316px) */
  MIDDLE_END: 200,

  /** Bottom offset from bottom edge (12.7% of height = 40px / 316px) */
  BOTTOM_OFFSET: 40,

  /** Default center position for single handles (50% of height = 158px / 316px) */
  DEFAULT_CENTER: 158,

  /**
   * Calculate Y position for Nth handle in a vertically distributed list
   * @param index - Handle index (0-based)
   * @param totalHandles - Total number of handles
   * @returns Y position in pixels
   */
  calculateDistributedPosition(index: number, totalHandles: number): number {
    if (totalHandles === 1) {
      return this.DEFAULT_CENTER;
    }

    const range = this.MIDDLE_END - this.MIDDLE_START;
    const spacing = range / (totalHandles - 1);
    return this.MIDDLE_START + (index * spacing);
  },
} as const;
